import { theme } from '@/brand-gbf/theme';
import { Pressable, SxProp, useDripsyTheme, View } from 'dripsy';
import React from 'react';
import { Icon } from '../icons/Icon';
import { TextCaption } from '../typography';
import { useTranslation } from 'react-i18next';

interface TokenPaginationProps {
  prevPageToken?: string;
  nextPageToken?: string;
  wrapperSx?: SxProp;
  buttonSx?: SxProp;
  onPrevPage?: () => void;
  onNextPage?: () => void;
}

export const TokenPagination: React.FC<TokenPaginationProps> = ({
  prevPageToken,
  nextPageToken,
  wrapperSx,
  buttonSx,
  onPrevPage,
  onNextPage
}) => {
  return (
    <View sx={{ ...styles.container, ...wrapperSx }}>
      {!!prevPageToken && <PaginationButton buttonSx={buttonSx} buttonType="prev" onPress={onPrevPage} />}
      {prevPageToken && nextPageToken && <View sx={{ width: '$2' }} />}
      {!!nextPageToken && <PaginationButton buttonSx={buttonSx} buttonType="next" onPress={onNextPage} />}
    </View>
  );
};

export const PaginationButton: React.FC<{
  buttonSx?: SxProp;
  buttonType: 'prev' | 'next';
  onPress?: () => void;
}> = ({ buttonSx, buttonType, onPress }) => {
  const { theme } = useDripsyTheme();
  const { t } = useTranslation('earn');
  const label = buttonType === 'prev' ? t('earn-invest::glossary.previous') : t('earn-invest::glossary.next');
  return (
    <Pressable
      sx={{ ...styles.button, ...buttonSx, pl: buttonType === 'prev' ? '$2' : '$3', pr: buttonType === 'next' ? '$2' : '$3' }}
      onPress={onPress}>
      {buttonType === 'prev' && (
        <React.Fragment>
          <Icon name="arrowLeft" color={theme.colors?.$paginationText} themeColor={theme.colors?.$paginationText} />
          <View sx={{ width: 5 }} />
        </React.Fragment>
      )}
      <TextCaption sx={{ color: '$paginationText' }}>{label}</TextCaption>
      {buttonType === 'next' && (
        <React.Fragment>
          <View sx={{ width: 5 }} />
          <Icon name="arrowRight" color={theme.colors?.$paginationText} themeColor={theme.colors?.$paginationText} />
        </React.Fragment>
      )}
    </Pressable>
  );
};

enum StyleName {
  container = 'container',
  button = 'button'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '$3',
    paddingY: '$2',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '$paginationBorder'
  }
};
